import type { ReactNode } from "react";
import { Box } from "@mui/material";

/**
 * Width of the docked panel. Wide enough for the consultation UI (which is laid
 * out for a dialog, not a sidebar) while still leaving a usable form beside it on
 * a laptop screen. Exported because whatever gives up the width to make room has
 * to agree with it.
 *
 * There is no narrow-screen variant: below the docking breakpoint the dictation
 * flow uses a dialog instead of this panel.
 */
export const TORTUS_DRAWER_WIDTH = 520;

export type TortusDockedPanelProps = {
  children: ReactNode;
  /**
   * Distance to keep from the top of the scrollport while stuck. Anything else
   * sticking there (a patient banner) sits at a higher z-index and would hide the
   * top of this panel behind it.
   */
  stickyTop?: number;
  /**
   * Distance to leave clear at the bottom of the viewport. Anything sticking
   * there (a form footer) also outranks this panel, and would hide the bottom of
   * its contents behind it.
   */
  bottomInset?: number;
  testId?: string;
};

/**
 * The column the dictation flow occupies beside a form: sticky, bordered, and
 * sized to the gap between whatever chrome is stuck above and below it.
 *
 * Deliberately NOT an MUI Drawer. A drawer's paper is fixed to the viewport, so
 * it spans the full window height and rides over whatever is above the form
 * (patient banner, page chrome) no matter where it is mounted. Being in normal
 * flow keeps it inside the section that owns it.
 *
 * Shared by every stage of the flow — the embed and the result chooser — so the
 * panel neither moves nor resizes as the user advances through them.
 */
export const TortusDockedPanel = ({
  children,
  stickyTop = 8,
  bottomInset = 8,
  testId,
}: TortusDockedPanelProps) => (
  <Box
    data-testid={testId}
    sx={{
      width: TORTUS_DRAWER_WIDTH,
      flexShrink: 0,
      position: "sticky",
      top: stickyTop,
      alignSelf: "flex-start",
      // Fills the viewport between the stuck chrome rather than taking a share of
      // it: the embed is a fixed-layout consultation UI, so any height it does
      // not get becomes internal scrolling.
      height: `calc(100vh - ${String(stickyTop + bottomInset)}px)`,
      // The height above is the outer box, borders included — without this the
      // panel overshoots the viewport by its border wherever a CSS baseline
      // hasn't already set it.
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      bgcolor: "background.default",
      overflow: "hidden",
    }}
  >
    {children}
  </Box>
);
